"use client";
import { translate, translations } from "../../translations/index.js";
import { WHITE_SPACE } from "./regex.js";
function getValidationMessage(result, messagePrefix, lang) {
  if (!result) {
    return "";
  }
  const firstError = result.errorMessages[0];
  return [messagePrefix, firstError].map((message) => typeof message === "string" ? message : message(lang)).filter((x) => !!x).join(" ");
}
function stripWhitespace(value) {
  return value.replace(WHITE_SPACE, "");
}
function defaultPreValidation(value) {
  if (typeof value === "string") {
    return value.trim();
  }
  return value;
}
const defaultConfig = () => ({
  steps: [],
  preValidation: defaultPreValidation,
  messagePrefix: "",
  necessity: { isRequired: false }
});
const validation = (config) => ({
  ...defaultConfig(),
  ...config
});
function runValidation(rawValue, allValues, { necessity, preValidation, steps, showMessageImmediately }) {
  const necessityConfig = typeof necessity === "function" ? necessity(allValues) : necessity;
  const isRequired = necessityConfig.isRequired ?? false;
  const value = preValidation(rawValue);
  const valueIsMissing = value === void 0 || value === null || typeof value === "string" && value === "" || typeof value === "object" && Array.isArray(value) && !value.length;
  if (valueIsMissing) {
    return {
      isValid: !isRequired,
      value,
      errorMessages: necessityConfig.isRequired ? [
        necessityConfig.missingMessage ?? translate((t) => t.validation.defaultRequiredError)
      ] : [],
      showMessageImmediately: false
    };
  }
  const validations = steps.map((f) => f(value, allValues));
  return {
    isValid: validations.every((x) => x.isValid),
    value,
    errorMessages: validations.filter((x) => !x.isValid).map((x) => x.errorMessage),
    showMessageImmediately: (showMessageImmediately == null ? void 0 : showMessageImmediately(value)) ?? false
  };
}
const required = (validator = validation(), message) => ({
  ...validator,
  necessity: {
    isRequired: true,
    missingMessage: message
  }
});
const createObjectValidator = (validators) => (values) => {
  const results = Object.fromEntries(Object.keys(values).map((key) => {
    const typedKey = key;
    return [
      typedKey,
      runValidation(values[typedKey], values, validators[typedKey])
    ];
  }));
  const entries = Object.entries(results);
  const isValid = entries.every(([, x]) => x.isValid);
  return {
    isValid,
    transformed: Object.fromEntries(entries.map(([key, validation2]) => [key, validation2.value])),
    validated: Object.fromEntries(entries.filter(([, validation2]) => validation2.isValid).map(([key, validation2]) => [key, validation2.value])),
    validation: results
  };
};
function initialUserInput(obj, validator) {
  const validated = validator(obj);
  return {
    raw: obj,
    showError: {},
    ...validated
  };
}
function getFieldNecessities(values, validators) {
  const fieldNecessities = new Set(Object.keys(validators).map((x) => {
    const necessity = validators[x].necessity;
    const necessityConfig = typeof necessity === "function" ? necessity(values) : necessity;
    return necessityConfig.isRequired ? "required" : "optional";
  }));
  return fieldNecessities.size === 1 ? Array.from(fieldNecessities)[0] : "mixed";
}
function addNecessityIndicator(values, validators, showNecessityOn, lang, helperText, field) {
  if (!field) {
    return helperText;
  }
  const allSameNecessity = getFieldNecessities(values, validators) !== "mixed";
  if (allSameNecessity) {
    return helperText;
  }
  const validation2 = validators[field];
  const necessityConfig = typeof validation2.necessity === "function" ? validation2.necessity(values) : validation2.necessity;
  if (showNecessityOn === "optional" && !necessityConfig.isRequired) {
    return [translations[lang].validation.optional, helperText].filter((x) => !!x).join(". ");
  }
  if (showNecessityOn === "required" && necessityConfig.isRequired) {
    return [translations[lang].validation.required, helperText].filter((x) => !!x).join(". ");
  }
  return helperText;
}
export {
  addNecessityIndicator,
  createObjectValidator,
  getFieldNecessities,
  getValidationMessage,
  initialUserInput,
  required,
  runValidation,
  stripWhitespace,
  validation
};
